/**
 * Layer 1 — Modules. Major areas of the application. `routeManifestIds` tie
 * each module to `shared/command-center/route-manifest.ts` so navigation and
 * route gating can resolve canonical paths. Modules depend on the capabilities
 * and services they are built from; disabling a module cascades to its journeys.
 */
import { MaturityLevel, defineModule } from '@/lib/capabilities/contracts';

export const modules = {
  'modules.assets': defineModule({
    key: 'modules.assets',
    layer: 'module',
    title: 'Assets',
    description: 'Asset register and lifecycle.',
    dependsOn: ['services.search', 'services.storage'],
    provides: ['module_assets'],
    requires: ['search', 'file_storage'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Simple,
    betaFlag: false,
    internalOnly: false,
    tags: ['operate'],
    active: true,
    routeManifestIds: ['assets'],
  }),
  'modules.sites': defineModule({
    key: 'modules.sites',
    layer: 'module',
    title: 'Sites',
    description: 'Site grouping and site-level rollups.',
    dependsOn: ['modules.assets'],
    provides: ['module_sites'],
    requires: ['module_assets'],
    minEdition: 'professional',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['operate'],
    active: true,
    routeManifestIds: ['assets'],
  }),
  'modules.requests': defineModule({
    key: 'modules.requests',
    layer: 'module',
    title: 'Requests',
    description: 'Inbound demand capture and triage.',
    dependsOn: ['capabilities.approvals'],
    provides: ['module_requests'],
    requires: ['approvals'],
    minEdition: 'professional',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['operate'],
    active: true,
    routeManifestIds: ['requests'],
  }),
  'modules.inspections': defineModule({
    key: 'modules.inspections',
    layer: 'module',
    title: 'Inspections',
    description: 'Inspection execution and quality operations.',
    dependsOn: ['modules.assets', 'capabilities.photo_capture'],
    provides: ['module_inspections'],
    requires: ['module_assets', 'evidence_media'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Simple,
    betaFlag: false,
    internalOnly: false,
    tags: ['operate'],
    active: true,
    routeManifestIds: ['inspections'],
  }),
  'modules.jobs': defineModule({
    key: 'modules.jobs',
    layer: 'module',
    title: 'Jobs',
    description: 'Job register, assignment and execution.',
    dependsOn: ['modules.assets'],
    provides: ['module_jobs'],
    requires: ['module_assets'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['operate'],
    active: true,
    routeManifestIds: ['jobs'],
  }),
  'modules.reviews': defineModule({
    key: 'modules.reviews',
    alwaysIncluded: 'Follows Approvals: on when Approvals is on.',
    layer: 'module',
    title: 'Reviews',
    description: 'Completion reviews and sign-off.',
    dependsOn: ['capabilities.approvals'],
    provides: ['module_reviews'],
    requires: ['approvals'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['operate'],
    active: true,
    routeManifestIds: ['inspections'],
  }),
  'modules.reports': defineModule({
    key: 'modules.reports',
    layer: 'module',
    title: 'Reports',
    description: 'Asset and site reporting and exports.',
    dependsOn: ['services.reporting_engine'],
    provides: ['module_reports'],
    requires: ['reporting'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['insights'],
    active: true,
    routeManifestIds: ['analytics'],
  }),
  'modules.analytics': defineModule({
    key: 'modules.analytics',
    layer: 'module',
    // The app calls this tab "Insights"; the Admin matrix must use the same word.
    title: 'Insights',
    description: 'Insights tab: operational analytics, KPIs and journey insight.',
    dependsOn: ['services.reporting_engine'],
    provides: ['module_analytics'],
    requires: ['reporting'],
    minEdition: 'professional',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['insights'],
    active: true,
    routeManifestIds: ['analytics'],
  }),
  'modules.contacts': defineModule({
    key: 'modules.contacts',
    layer: 'module',
    title: 'Contacts',
    description: 'External people and organisations.',
    dependsOn: ['services.search'],
    provides: ['module_contacts'],
    requires: ['search'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['network'],
    active: true,
    routeManifestIds: ['contacts'],
  }),
  'modules.contractors': defineModule({
    key: 'modules.contractors',
    layer: 'module',
    title: 'Contractors',
    description: 'Contractor directory and engagement.',
    dependsOn: ['modules.contacts'],
    provides: ['module_contractors'],
    requires: ['module_contacts'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['network'],
    active: true,
    routeManifestIds: ['contractors', 'contractor-home'],
  }),
  'modules.teams': defineModule({
    key: 'modules.teams',
    layer: 'module',
    title: 'Teams',
    description: 'Team ownership, membership and invites.',
    dependsOn: ['services.permissions'],
    provides: ['module_teams'],
    requires: ['authorization'],
    minEdition: 'professional',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['network'],
    active: true,
    routeManifestIds: ['teams'],
  }),
  'modules.automation': defineModule({
    key: 'modules.automation',
    layer: 'module',
    title: 'Automation',
    description: 'Workflow automation and recurring work.',
    dependsOn: ['services.automation_engine'],
    provides: ['module_automation'],
    requires: ['automation'],
    minEdition: 'enterprise',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['operate'],
    active: true,
    routeManifestIds: ['integrations'],
  }),
  'modules.integrations': defineModule({
    key: 'modules.integrations',
    layer: 'module',
    title: 'Integrations',
    description: 'APIs, webhooks, service accounts and imports.',
    dependsOn: ['services.integration_engine'],
    provides: ['module_integrations'],
    requires: ['integration'],
    // Business (20261231490000); mirrors the Matrix, and a test keeps them equal.
    minEdition: 'enterprise',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['operate'],
    active: true,
    routeManifestIds: ['integrations'],
  }),
  'modules.ai': defineModule({
    key: 'modules.ai',
    alwaysIncluded: 'A heading: each AI feature has its own switch.',
    layer: 'module',
    title: 'AI',
    description: 'AI assistants, summaries and intelligence surfaces.',
    dependsOn: ['services.ai_platform'],
    provides: ['module_ai'],
    requires: ['ai_inference'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['operate'],
    active: true,
    routeManifestIds: [],
  }),
  'modules.administration': defineModule({
    key: 'modules.administration',
    alwaysIncluded: 'Core: every workspace manages its people and account.',
    layer: 'module',
    title: 'Administration',
    description: 'Trust centre, capability management and workspace admin.',
    dependsOn: ['services.permissions', 'services.audit'],
    provides: ['module_admin'],
    requires: ['authorization', 'audit_trail'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Simple,
    betaFlag: false,
    internalOnly: false,
    tags: ['account'],
    active: true,
    routeManifestIds: ['account-workspace'],
  }),
  'modules.settings': defineModule({
    key: 'modules.settings',
    alwaysIncluded: 'Core: every workspace has its settings.',
    layer: 'module',
    title: 'Settings',
    description: 'Personal and workspace preferences.',
    dependsOn: ['services.auth'],
    provides: ['module_settings'],
    requires: ['identity'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Simple,
    betaFlag: false,
    internalOnly: false,
    tags: ['account'],
    active: true,
    routeManifestIds: ['account-settings'],
  }),
  'modules.developer': defineModule({
    key: 'modules.developer',
    layer: 'module',
    title: 'Developer',
    description: 'Developer tooling and design-system surfaces.',
    dependsOn: ['services.api_platform'],
    provides: ['module_developer'],
    requires: ['public_api'],
    minEdition: 'enterprise',
    maturityLevel: MaturityLevel.Enterprise,
    betaFlag: false,
    internalOnly: false,
    tags: ['account', 'developer'],
    active: true,
    routeManifestIds: [],
  }),
} as const;
